import { AnalyticsCache, ClientAnalytics, AgentAnalytics } from '../types/business';

export class BusinessAnalytics {
  private static readonly CACHE_PREFIX = 'businessAnalytics';
  private static readonly CACHE_VERSION = '2'; // Increment when data structure changes
  
  /**
   * Generate cache key for analytics data
   */
  private static getCacheKey(businessId: string, type: 'client' | 'agent', identifier: string, params?: Record<string, any>): string {
    const paramString = params ? '_' + Object.entries(params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${value}`)
      .join('_') : '';
    
    return `${this.CACHE_PREFIX}_v${this.CACHE_VERSION}_${businessId}_${type}_${identifier}${paramString}`;
  }

  /**
   * Calculate next UTC 12:00 expiration time
   */
  private static getNextExpirationTime(): number {
    const now = new Date();
    const nextExpiry = new Date();
    
    // Set to next UTC 12:00
    nextExpiry.setUTCHours(12, 0, 0, 0);
    
    // If we're past 12:00 UTC today, move to tomorrow
    if (now.getTime() >= nextExpiry.getTime()) {
      nextExpiry.setUTCDate(nextExpiry.getUTCDate() + 1);
    }
    
    return nextExpiry.getTime();
  }

  /**
   * Get cached analytics data
   */
  static getCachedData<T extends ClientAnalytics | AgentAnalytics>(
    businessId: string, 
    type: 'client' | 'agent', 
    identifier: string,
    params?: Record<string, any>
  ): T | null {
    try {
      const cacheKey = this.getCacheKey(businessId, type, identifier, params);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const cacheData: AnalyticsCache = JSON.parse(cached);
      
      // Check if expired
      if (Date.now() > cacheData.expiresAt) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return cacheData.data as T;
    } catch (error) {
      console.error('Error reading analytics cache:', error);
      return null;
    }
  }

  /**
   * Set cached analytics data
   */
  static setCachedData<T extends ClientAnalytics | AgentAnalytics>(
    businessId: string,
    type: 'client' | 'agent',
    identifier: string,
    data: T,
    params?: Record<string, any>
  ): void {
    try {
      const cacheKey = this.getCacheKey(businessId, type, identifier, params);
      const cacheData: AnalyticsCache = {
        data,
        timestamp: Date.now(),
        expiresAt: this.getNextExpirationTime()
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error writing analytics cache:', error);
    }
  }

  /**
   * Get cache information
   */
  static getCacheInfo(
    businessId: string,
    type: 'client' | 'agent',
    identifier: string,
    params?: Record<string, any>
  ): { isStale: boolean; lastFetched: Date | null } {
    try {
      const cacheKey = this.getCacheKey(businessId, type, identifier, params);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return { isStale: true, lastFetched: null };
      }
      
      const cacheData: AnalyticsCache = JSON.parse(cached);
      const isStale = Date.now() > cacheData.expiresAt;
      const lastFetched = new Date(cacheData.timestamp);
      
      return { isStale, lastFetched };
    } catch (error) {
      console.error('Error reading cache info:', error);
      return { isStale: true, lastFetched: null };
    }
  }

  /**
   * Clear specific cache entry
   */
  static clearCache(
    businessId: string,
    type: 'client' | 'agent',
    identifier: string,
    params?: Record<string, any>
  ): void {
    try {
      const cacheKey = this.getCacheKey(businessId, type, identifier, params);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  /**
   * Clear all analytics cache for a business
   */
  static clearAllCache(businessId: string): void {
    try {
      const prefix = `${this.CACHE_PREFIX}_${businessId}_`;
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing all cache:', error);
    }
  }

  /**
   * Clean up expired cache entries and old versions
   */
  static cleanupExpiredCache(): void {
    try {
      const keysToRemove: string[] = [];
      const now = Date.now();
      const currentVersionPrefix = `${this.CACHE_PREFIX}_v${this.CACHE_VERSION}_`;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_PREFIX)) {
          // Remove old version caches immediately
          if (!key.startsWith(currentVersionPrefix)) {
            keysToRemove.push(key);
            continue;
          }
          
          // Check if current version cache is expired
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const cacheData: AnalyticsCache = JSON.parse(cached);
              if (now > cacheData.expiresAt) {
                keysToRemove.push(key);
              }
            }
          } catch {
            // Invalid cache entry, remove it
            keysToRemove.push(key);
          }
        }
      }
      
      if (keysToRemove.length > 0) {
        console.log(`Cleaning up ${keysToRemove.length} cache entries (including old versions)`);
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
    } catch (error) {
      console.error('Error cleaning up expired cache:', error);
    }
  }
}